import {
  FilterCheckbox,
  FilterCheckRow,
  FilterMenu,
  FilterMenuClear,
  FilterMenuEmpty,
  FilterMenuSearch,
  FilterPill,
  FilterPillCount,
  FilterPillLabel,
  FilterRowLabel,
} from '@src/components/common/filterDropdownStyles';
import { useAnchoredDropdown } from '@src/components/common/useAnchoredDropdown';
import CheckIconAlt from '@src/svgs/CheckIconAlt';
import ChevronDownIcon from '@src/svgs/ChevronDownIcon';
import { useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

// A compact, reusable multi-select facet filter in the design system's filter
// pill pattern (the homepage's Recent Content filters): a labelled pill with a
// muted selection count and a gold caret that opens a portaled checkbox list
// (via the shared useAnchoredDropdown). Used above the voice picker for
// Gender / Language / Provider. An empty selection means "no filter".
export interface MultiSelectOption {
  value: string;
  label: string;
}

interface MultiSelectFilterProps {
  label: string;
  options: MultiSelectOption[];
  selected: string[];
  onChange: (values: string[]) => void;
  /** Show a filter input above the list (for long option sets, e.g. languages). */
  searchable?: boolean;
  /** Menu min-width in px (defaults to 160; widen for long labels). */
  minWidth?: number;
  disabled?: boolean;
}

const MultiSelectFilter = ({
  label,
  options,
  selected,
  onChange,
  searchable = false,
  minWidth = 160,
  disabled = false,
}: MultiSelectFilterProps) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const triggerRef = useRef<HTMLButtonElement>(null);
  const { menuRef, style } = useAnchoredDropdown(
    open,
    triggerRef,
    () => setOpen(false),
    340,
  );

  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? options.filter(o => o.label.toLowerCase().includes(q)) : options;
  }, [options, query]);

  const toggle = (value: string) => {
    onChange(
      selectedSet.has(value)
        ? selected.filter(v => v !== value)
        : [...selected, value],
    );
  };

  return (
    <>
      <FilterPill
        type="button"
        ref={triggerRef}
        disabled={disabled || options.length === 0}
        $active={selected.length > 0}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
      >
        <FilterPillLabel>{label}</FilterPillLabel>
        {selected.length > 0 && (
          <FilterPillCount>{selected.length}</FilterPillCount>
        )}
        <ChevronDownIcon strokeColor="var(--accent)" />
      </FilterPill>

      {open &&
        style &&
        createPortal(
          <FilterMenu
            ref={menuRef}
            style={{ ...style, minWidth }}
            role="listbox"
            aria-multiselectable
          >
            {searchable && (
              <FilterMenuSearch
                type="text"
                value={query}
                placeholder={`Filter ${label.toLowerCase()}…`}
                aria-label={`Filter ${label}`}
                onChange={e => setQuery(e.target.value)}
              />
            )}
            {selected.length > 0 && (
              <FilterMenuClear type="button" onClick={() => onChange([])}>
                Clear {label.toLowerCase()}
              </FilterMenuClear>
            )}
            {filtered.map(o => {
              const isSel = selectedSet.has(o.value);
              return (
                <FilterCheckRow
                  key={o.value}
                  type="button"
                  role="option"
                  aria-selected={isSel}
                  onClick={() => toggle(o.value)}
                >
                  <FilterCheckbox $checked={isSel}>
                    {isSel && <CheckIconAlt />}
                  </FilterCheckbox>
                  <FilterRowLabel>{o.label}</FilterRowLabel>
                </FilterCheckRow>
              );
            })}
            {filtered.length === 0 && (
              <FilterMenuEmpty>No matches</FilterMenuEmpty>
            )}
          </FilterMenu>,
          document.body,
        )}
    </>
  );
};

export default MultiSelectFilter;
